
import { Component, Vue, Watch } from 'vue-property-decorator';

import SelectField from '../../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../../framework/state';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../../framework/client/resource';
import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';

import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';

import { PurchaseInvoice } from '../../../../../../common/application/model/PurchaseInvoice';
import { getVendorOptions } from '../../../../service/application_options_service';
import {
    getPurchaseInvoiceById,
    getPurchaseInvoiceList,
    postPurchaseInvoiceAccrualRule,
    putPurchaseInvoice,
    validatePurchaseInvoice,
} from '../../../../client/netvisor';
import { PurchaseInvoiceState } from '../../../../../../common/application/model/enums/PurchaseInvoiceState';
import { getTagTypeFromPurchaseInvoiceState } from '../../../../service/tag_type_service';
import { SalesInvoiceRow } from '../../../../../../common/application/model/SalesInvoiceRow';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { NetvisorApprovalStatus } from '../../../../../../common/application/model/enums/NetvisorApprovalStatus';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import { errorToast, successToast } from '../../../../service/toast_service';
import { formatCurrency } from '../../../../service/helper_service';
import ParameterService from '../../../../service/parameter_service';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
    },
})
export default class PurchaseInvoices extends Vue {
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as PurchaseInvoice[],
        total: 0,
        loading: false,
        selected: undefined as PurchaseInvoice | undefined,
        selectedVendorId: undefined as string | undefined,
        selectedPurchaseInvoiceState: undefined as string | undefined,
        selectedPurchaseInvoiceId: undefined as string | undefined,
        purchaseInvoiceStateOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.purchaseInvoiceStateOptions = getEnumerationOptions(
            'PurchaseInvoiceState',
            Object.keys(PurchaseInvoiceState),
            this,
        );
        this.local.vendorOptions = await getVendorOptions();
        await this.loadAsyncData();
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];

        const rows: PurchaseInvoice[] = await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.parameters.purchaseInvoiceState')
    async selectedPurchaseInvoiceStateChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.parameters.id')
    async selectedPurchaseInvoiceIdChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    onRowClick(row: PurchaseInvoice) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    add() {
        this.$router.push(`/${this.resourceType}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        const rows = await getResourcesNew<SalesInvoiceRow>(
                            ApplicationResource.PURCHASE_INVOICE_ROW,
                            -1,
                            new Map([['purchaseInvoiceId', this.local.selected.id]]),
                        );
                        for (const row of rows) {
                            await deleteResource(ApplicationResource.PURCHASE_INVOICE_ROW, row.id);
                        }

                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async getNetvisorInfo(invoiceId: string) {
        try {
            this.local.loading = true;
            await getPurchaseInvoiceById(invoiceId);
            await this.loadAsyncData();
            this.local.loading = false;
        } catch (error) {
            this.local.loading = false;
        }
    }

    async sendToNetvisor(invoiceId: string) {
        try {
            this.local.loading = true;

            const isValid = await validatePurchaseInvoice(invoiceId);

            if (isValid) {
                const result = await putPurchaseInvoice(invoiceId);

                if (result.responseStatus.status === 'FAILED') {
                    this.$buefy.toast.open({
                        message: this.$t(result.replies.insertedDataIdentifier).toString(),
                        type: 'is-danger',
                        position: 'is-top',
                    });
                }

                await getPurchaseInvoiceById(invoiceId);
            } else {
                errorToast(this, 'message.invoiceSumsNotEqual');
            }
            this.local.loading = false;
            await this.loadAsyncData();
        } catch (error) {
            this.local.loading = false;
        }
    }

    canPostAccrualRule(): boolean {
        if (this.local.selected && this.local.selected.id) {
            if (
                this.local.selected.purchaseInvoiceState === PurchaseInvoiceState.ACCEPTED &&
                !this.local.selected.accrualRuleApplied
            ) {
                return true;
            }
        }
        return false;
    }

    async postAccrualRuleToNetvisor(invoiceId: string) {
        try {
            this.local.loading = true;
            const result = await postPurchaseInvoiceAccrualRule(invoiceId);

            if (result.responseStatus.status === 'FAILED') {
                errorToast(this);
            }

            await getPurchaseInvoiceById(invoiceId);
            this.local.loading = false;
            await this.loadAsyncData();
        } catch (error) {
            this.local.loading = false;
        }
    }

    async getOpenInvoices() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirm').toString(),
            message: this.$t('message.areYouSure').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                try {
                    this.local.loading = true;
                    await getPurchaseInvoiceList(NetvisorApprovalStatus.OPEN);
                    await this.loadAsyncData();
                    this.local.loading = false;
                } catch (e) {
                    errorToast(this);
                }
            },
        });
    }

    getVendorName(id: string): string {
        return getOptionLabel(id, this.local.vendorOptions);
    }

    getPurchaseInvoiceStateLabel(id: string): string {
        return getOptionLabel(id, this.local.purchaseInvoiceStateOptions);
    }

    getTagType(id: PurchaseInvoiceState): string {
        return getTagTypeFromPurchaseInvoiceState(id);
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${ApplicationResource.PURCHASE_INVOICE}/${this.local.selected.id}`);
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    get purchaseInvoiceId(): string[] {
        return this.local.parameters.id ? [this.local.parameters.id] : [];
    }

    get vendorId(): string[] {
        return this.local.parameters.vendorId ? [this.local.parameters.vendorId] : [];
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    purchaseInvoiceParameters(): Map<string, any> {
        const parameters = new Map<string, any>();

        if (this.local.parameters.vendorId && this.local.parameters.vendorId.length > 0) {
            parameters.set('vendorId', this.local.parameters.vendorId);
        }

        return parameters;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
